import cn from 'classnames';
import React, { useEffect, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

// CSS
import styles from '@UILayer/UILayer.scss';

// REDUX
import { getCurrentGameState, getGameHistoryState, getGlobalStyles, setInGameHistoryMode } from '@Managers/Redux/AppState/AppState';
import { useAppDispatch, useAppSelector } from '@Managers/Redux/Hooks';
import { Button } from '@UILayer/Inputs/Button';

// IMAGES
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import SliderComponent from '@UILayer/Inputs/SliderComponent/SliderComponent';
import { DOM_ID } from '@UILayer/UILayer.types';
import moment from 'moment';
import InGameHistoryPanelNavigation from './InGameHistoryPanel.navigation';

const InGameHistoryPanel: React.FC<{}> = () => {
    const [state, setState] = useReducer(
        (state: { currentIndex: number; totalSteps: number; currentID: string; spinTime: string; duringSpin: boolean }, newState: any) => ({
            ...state,
            ...newState
        }),
        {
            currentIndex: 0,
            totalSteps: 0,
            currentID: '',
            spinTime: '',
            duringSpin: false
        }
    );

    const globalStyles = useAppSelector(getGlobalStyles);
    const gameStoreGameHistoryState = useAppSelector(getGameHistoryState);
    const gameStoreCurrentGameState = useAppSelector(getCurrentGameState);

    const dispatch = useAppDispatch();
    const classes = cn(styles.inGameHistoryPanel_container, globalStyles.orientation, globalStyles.wcaMode, globalStyles.screenSize, globalStyles.isTablet);
    const { t } = useTranslation();

    useEffect(() => {
        EventsCenter.emit(EGameEvents.INGAME_HISTORY_OPEN);
    }, []);

    useEffect(() => {
        const currentHistoryData = gameStoreGameHistoryState.spinsHistory[state.currentIndex];
        moment.locale(navigator.language);

        setState({
            totalSteps: gameStoreGameHistoryState.spinsHistory.length,
            currentIndex: gameStoreGameHistoryState.spinNr,
            currentID: currentHistoryData.cycleId,
            spinTime: moment(currentHistoryData.timeStamp).format('HH:mm:ss, L'),
            duringSpin: gameStoreCurrentGameState !== EGameStates.IDLE_STATE ? true : false
        });
    }, [gameStoreGameHistoryState, gameStoreCurrentGameState]);

    function changeComplete(value: number) {
        if (value <= state.totalSteps) {
            EventsCenter.emit(EGameEvents.INGAME_HISTORY_INDEX_CHANGE, { index: value });
        }
    }

    function close() {
        if (!state.duringSpin) {
            EventsCenter.emit(EGameEvents.INGAME_HISTORY_CLOSE);
            dispatch(setInGameHistoryMode(false));
        }
    }

    return (
        <>
            <div className={classes}>
                <div className={styles.inGameHistoryPanel_header}>
                    <div>
                        <span className={styles.inGameHistoryPanel_label}>{t('INGAME_HISTORY_PANEL_CYCLE_ID')}:</span> {state.currentID} <br />
                        <span className={styles.inGameHistoryPanel_label}>{t('INGAME_HISTORY_PANEL_CYCLE_TIME')}:</span> {state.spinTime}
                    </div>
                    <Button
                        id={DOM_ID.BUTTON_CLOSE_INGAME_HISTORY_PANEL}
                        description={t('INGAME_HISTORY_PANEL_CLOSE_BUTTON_WCA_DESCRIPTION')}
                        className={cn(styles.close_button, state.duringSpin ? styles.disabled : false)}
                        onChange={() => close()}
                        uiFreezeCheck={false}
                    ></Button>
                </div>

                <InGameHistoryPanelNavigation currentIndex={state.currentIndex} totalSteps={state.totalSteps} />

                {state.totalSteps > 1 && (
                    <div className={styles.inGameHistoryPanel_slider}>
                        <SliderComponent
                            disabled={state.duringSpin}
                            min={0}
                            defaultValue={state.currentIndex}
                            changeComplete={(value) => changeComplete(value)}
                            showValueBox={false}
                            max={state.totalSteps - 1}
                        />
                    </div>
                )}
            </div>

            <div className={styles.inGameHistoryNote}>{t('INGAME_HISTORY_PANEL_NOTE')}</div>
        </>
    );
};

export default InGameHistoryPanel;
